import { Loader2 } from "lucide-react";
import type { ReactNode } from "react";
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { MAX_CAMPAIGN_SYSTEM_INSTRUCTIONS_LENGTH } from "@/lib/constants";
import type { LaunchCharacterSummary } from "@/lib/core/schemas/launch-hub";
import { LaunchCampaignError, launchCampaign } from "@/src/api/campaigns";

const STALE_ASSIGNMENT_MESSAGE =
  "That character was just assigned to another campaign.";
const CONTENT_INCOMPATIBLE_MESSAGE =
  "This character's stored build no longer matches your saved version. Recreate the character and try again.";

type CreateCampaignModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated: (campaignId: string) => void;
  /**
   * The hub's character list (D7): ALL characters, including
   * non-selectable ones, which render disabled with a reason.
   */
  characters: readonly LaunchCharacterSummary[];
  /** Preselects this character when it is in the selectable set. */
  initialCharacterId?: string;
  /**
   * Fired when a launch failure indicates the selected character's
   * assignment state is stale (someone else raced the launch) — the hub
   * wires this to its whole-model `refresh()`.
   */
  onStale?: () => void;
};

type CharacterOption = {
  id: string;
  name: string;
  selectable: boolean;
  reasonSuffix: string | null;
};

function reasonSuffixFor(character: LaunchCharacterSummary): string | null {
  if (character.status.kind === "assigned") {
    return character.status.campaignArchived
      ? `— in archived ${character.status.campaignName}`
      : `— in ${character.status.campaignName}`;
  }
  if (character.launchSupport.kind !== "supported") {
    return "— version unsupported";
  }
  return null;
}

function toCharacterOption(character: LaunchCharacterSummary): CharacterOption {
  const reasonSuffix = reasonSuffixFor(character);
  return {
    id: character.id,
    name: character.name,
    selectable: reasonSuffix === null,
    reasonSuffix,
  };
}

export function CreateCampaignModal({
  open,
  onOpenChange,
  onCreated,
  characters,
  initialCharacterId,
  onStale,
}: CreateCampaignModalProps) {
  const [name, setName] = useState("");
  const [systemInstructions, setSystemInstructions] = useState("");
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [selectedCharacterId, setSelectedCharacterId] = useState("");
  const [isPending, setIsPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const characterOptions = useMemo<CharacterOption[]>(
    () => characters.map(toCharacterOption),
    [characters]
  );

  const selectableCount = characterOptions.filter((o) => o.selectable).length;

  // Preselect the character passed by the Characters-tab "Launch a
  // campaign" affordance, but only if it's actually selectable — an
  // unselectable preselection would silently leave Create disabled with no
  // explanation.
  useEffect(() => {
    if (!initialCharacterId || selectedCharacterId) {
      return;
    }
    const match = characterOptions.find(
      (o) => o.id === initialCharacterId && o.selectable
    );
    if (match) {
      setSelectedCharacterId(match.id);
    }
  }, [initialCharacterId, characterOptions, selectedCharacterId]);

  let characterPicker: ReactNode;
  if (selectableCount === 0) {
    characterPicker = (
      <p className="text-muted-foreground text-sm">
        You don't have any available characters.{" "}
        <Link className="underline" to="/characters/new">
          Create a character first
        </Link>
        .
      </p>
    );
  } else {
    characterPicker = (
      <Select
        disabled={isPending}
        onValueChange={setSelectedCharacterId}
        value={selectedCharacterId}
      >
        <SelectTrigger aria-label="Character">
          <SelectValue placeholder="Select a character" />
        </SelectTrigger>
        <SelectContent>
          {characterOptions.map((o) => (
            <SelectItem disabled={!o.selectable} key={o.id} value={o.id}>
              {o.reasonSuffix ? `${o.name} ${o.reasonSuffix}` : o.name}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    );
  }

  const handleCreate = async () => {
    const trimmedName = name.trim();
    if (!(trimmedName && selectedCharacterId)) {
      return;
    }
    const trimmedInstructions = systemInstructions.trim();
    setIsPending(true);
    setError(null);
    try {
      const result = await launchCampaign({
        characterId: selectedCharacterId,
        name: trimmedName,
        ...(trimmedInstructions
          ? { systemInstructions: trimmedInstructions }
          : {}),
      });
      setName("");
      setSystemInstructions("");
      setSelectedCharacterId("");
      onCreated(result.campaign.id);
    } catch (err) {
      if (err instanceof LaunchCampaignError) {
        if (err.variant === "character_already_assigned") {
          if (err.existingCampaignId) {
            // Lost-response recovery: the launch actually succeeded on the
            // server (or a prior attempt did) — the tool only returns
            // existingCampaignId for the character just submitted, so this
            // is success-equivalent, not an error.
            onCreated(err.existingCampaignId);
          } else {
            setError(STALE_ASSIGNMENT_MESSAGE);
            onStale?.();
          }
        } else if (err.variant === "content_incompatible") {
          // Deliberately do not clear `name` — preserve what the user typed.
          setError(CONTENT_INCOMPATIBLE_MESSAGE);
        } else {
          setError(err.message);
        }
      } else {
        setError(
          err instanceof Error
            ? err.message
            : "Failed to launch campaign. Please try again."
        );
      }
    } finally {
      setIsPending(false);
    }
  };

  return (
    <Dialog onOpenChange={onOpenChange} open={open}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>New Campaign</DialogTitle>
          <DialogDescription>
            Pick a character and name your campaign.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <Input
            aria-label="Campaign name"
            autoFocus
            disabled={isPending}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                handleCreate();
              }
            }}
            placeholder="Campaign name"
            value={name}
          />
          {characterPicker}
          <Collapsible onOpenChange={setAdvancedOpen} open={advancedOpen}>
            <CollapsibleTrigger asChild>
              <Button size="sm" type="button" variant="ghost">
                Advanced
              </Button>
            </CollapsibleTrigger>
            <CollapsibleContent className="pt-2">
              <Textarea
                aria-label="System instructions"
                disabled={isPending}
                maxLength={MAX_CAMPAIGN_SYSTEM_INSTRUCTIONS_LENGTH}
                onChange={(e) => setSystemInstructions(e.target.value)}
                placeholder="Optional instructions for how the AI narrates this campaign."
                value={systemInstructions}
              />
            </CollapsibleContent>
          </Collapsible>
          {error && <p className="text-destructive text-sm">{error}</p>}
        </div>
        <DialogFooter>
          <Button
            disabled={isPending || !(name.trim() && selectedCharacterId)}
            onClick={handleCreate}
            type="button"
          >
            {isPending ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : null}
            Create
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
